/** Dibujo de cada tipo de figura en coordenadas de mundo. */
import { topicFontSize, topicPadding } from '../mindmap/actions.ts'
import type { DrawShape, GeoShape, ImageShape, NoteShape, Shape, TextShape, TopicShape } from '../../shared/types.ts'
import { getStrokeOutline, hasPressureVariation, outlineToPath, type InkPoint } from '../lib/freehand.ts'
import { getImage } from './images.ts'
import { drawLines, fitText, fontString, wrapText } from './text.ts'

/** Caché de contornos de tinta: recalcular el outline en cada cuadro es caro. */
const inkPathCache = new Map<string, { key: string; path: Path2D }>()

function inkCacheKey(shape: DrawShape): string {
  return `${shape.updatedAt}:${shape.w.toFixed(2)}:${shape.h.toFixed(2)}:${shape.size}:${shape.thinning ?? ''}`
}

export function inkPoints(shape: DrawShape): InkPoint[] {
  const sx = shape.baseW > 0 ? shape.w / shape.baseW : 1
  const sy = shape.baseH > 0 ? shape.h / shape.baseH : 1
  const points: InkPoint[] = []
  for (let i = 0; i < shape.points.length; i += 3) {
    points.push({
      x: shape.points[i] * sx,
      y: shape.points[i + 1] * sy,
      p: shape.points[i + 2],
    })
  }
  return points
}

export function drawPathFor(shape: DrawShape): Path2D {
  const key = inkCacheKey(shape)
  const cached = inkPathCache.get(shape.id)
  if (cached && cached.key === key) return cached.path
  const sx = shape.baseW > 0 ? shape.w / shape.baseW : 1
  const sy = shape.baseH > 0 ? shape.h / shape.baseH : 1
  const scale = (Math.abs(sx) + Math.abs(sy)) / 2
  const points = inkPoints(shape)
  const outline = getStrokeOutline(points, {
    size: shape.size * scale,
    last: true,
    flat: shape.highlighter,
    // Cada trazo recuerda con cuánta sensibilidad se dibujó. Los trazos viejos
    // (sin ese dato) siguen la regla de antes: con lápiz modula, con ratón casi no.
    thinning: shape.highlighter
      ? 0
      : (shape.thinning ?? (hasRealPressure(points) ? 0.55 : 0.22)),
    // Los puntos guardados ya vienen suavizados (posición y presión).
    streamline: 0,
  })
  const path = outlineToPath(outline)
  inkPathCache.set(shape.id, { key, path })
  if (inkPathCache.size > 4000) {
    // Poda simple: borra la mitad más antigua (Map conserva orden de inserción).
    let n = 0
    for (const k of inkPathCache.keys()) {
      inkPathCache.delete(k)
      if (++n > 1500) break
    }
  }
  return path
}

export function forgetShapeCache(id: string): void {
  inkPathCache.delete(id)
}

/** ¿El trazo trae presión real (lápiz) o todo vino con el valor neutro? */
export function hasRealPressure(points: InkPoint[]): boolean {
  return hasPressureVariation(points)
}

/** Aplica traslación + rotación para dibujar la figura en su sitio. */
function withTransform(ctx: CanvasRenderingContext2D, shape: Shape, fn: () => void): void {
  ctx.save()
  if (shape.rotation) {
    const cx = shape.x + shape.w / 2
    const cy = shape.y + shape.h / 2
    ctx.translate(cx, cy)
    ctx.rotate(shape.rotation)
    ctx.translate(-shape.w / 2, -shape.h / 2)
  } else {
    ctx.translate(shape.x, shape.y)
  }
  fn()
  ctx.restore()
}

/* ── Modo oscuro de exhibición ────────────────────────────────────────
   El modo oscuro NO toca los datos: un trazo negro sigue siendo negro en el
   tablero (quien esté en modo claro lo ve negro). Solo aquí, al pintar, los
   colores muy oscuros se muestran claros para que se lean sobre el fondo
   negro. Es lo que hace Microsoft Whiteboard con su "ink remapping". */

let darkDisplay = false

/** Lo fija el renderizador: true al pintar la vista, false en exportaciones. */
export function setDarkDisplay(on: boolean): void {
  darkDisplay = on
}

export function isDarkDisplay(): boolean {
  return darkDisplay
}

/**
 * Color con el que se EXHIBE una tinta según el modo (los datos no cambian).
 * Los oscuros casi neutros (negro, grises) se vuelven claros; los de color se
 * aclaran CONSERVANDO el matiz (un azul oscuro se vuelve azul claro, no gris).
 */
export function displayInk(color: string): string {
  return displayInkFor(color, darkDisplay)
}

/**
 * Igual que displayInk pero con el modo explícito. La bandera global solo la
 * actualiza el renderizador al pintar, así que quien no pinta (el puente al
 * iPad) debe pasar el modo del store: si no, al activar el modo oscuro la
 * capa nativa seguía con el color viejo y el trazo en vivo era invisible.
 */
export function displayInkFor(color: string, dark: boolean): string {
  if (!dark) return color
  const hex = color.startsWith('#') ? color.slice(1) : color
  if (hex.length !== 6) return color
  const n = parseInt(hex, 16)
  if (Number.isNaN(n)) return color
  const r = ((n >> 16) & 0xff) / 255
  const g = ((n >> 8) & 0xff) / 255
  const b = (n & 0xff) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (l >= 0.45) return color

  const d = max - min
  const sat = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1))
  if (sat < 0.2) return '#EDEDED'

  let h = 0
  if (d > 0) {
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
    else if (max === g) h = ((b - r) / d + 2) / 6
    else h = ((r - g) / d + 4) / 6
  }
  return hslToHex(h, Math.min(sat, 0.9), 0.66)
}

function hslToHex(h: number, s: number, l: number): string {
  const f = (offset: number): number => {
    const k = (offset + h * 12) % 12
    const a = s * Math.min(l, 1 - l)
    const value = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
    return Math.round(value * 255)
  }
  const to = (v: number): string => v.toString(16).padStart(2, '0')
  return `#${to(f(0))}${to(f(8))}${to(f(4))}`.toUpperCase()
}

export function renderShape(ctx: CanvasRenderingContext2D, shape: Shape, hideText = false): void {
  switch (shape.type) {
    case 'draw':
      renderDraw(ctx, shape)
      break
    case 'shape':
      withTransform(ctx, shape, () => renderGeo(ctx, shape, hideText))
      break
    case 'note':
      withTransform(ctx, shape, () => renderNote(ctx, shape, hideText))
      break
    case 'text':
      withTransform(ctx, shape, () => renderText(ctx, shape, hideText))
      break
    case 'image':
      withTransform(ctx, shape, () => renderImage(ctx, shape))
      break
    case 'topic':
      if (!shape.hidden) renderTopic(ctx, shape, hideText)
      break
    case 'relation':
      // Las relaciones se dibujan en la pasada de conectores del renderizador
      // (necesitan los dos extremos); aquí no hay nada que pintar.
      break
  }
}

/* ── Temas del mapa mental ───────────────────────────────────────────── */

/** Color del texto de un tema, según su nivel y el fondo que le toque. */
function topicTextColor(shape: TopicShape): string {
  if (shape.depth === 0) return '#FFFFFF'
  return displayInk('#242424')
}

function renderTopic(ctx: CanvasRenderingContext2D, shape: TopicShape, hideText: boolean): void {
  const branch = displayInk(shape.color ?? shape.branchColor)
  const r = shape.depth === 0 ? 14 : 10
  const path = new Path2D()
  path.roundRect(shape.x, shape.y, shape.w, shape.h, r)

  if (shape.depth === 0) {
    // Tema central: pastilla llena oscura (clara en modo oscuro no: siempre oscura
    // con texto blanco, se lee bien en ambos fondos).
    ctx.fillStyle = shape.color ?? '#26262B'
    ctx.fill(path)
  } else if (shape.depth === 1) {
    // Temas principales: tarjeta clara con borde del color de la rama.
    ctx.fillStyle = darkDisplay ? '#26262B' : '#FFFFFF'
    ctx.fill(path)
    ctx.strokeStyle = branch
    ctx.lineWidth = 2
    ctx.stroke(path)
  } else {
    // Subtemas: texto sobre una línea del color de la rama (estilo XMind).
    ctx.strokeStyle = branch
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(shape.x + 4, shape.y + shape.h)
    ctx.lineTo(shape.x + shape.w - 4, shape.y + shape.h)
    ctx.stroke()
  }

  if (!hideText && shape.text) {
    // fitText encoge la letra si hace falta: el ancho lo midió quien escribió,
    // y en otra plataforma (otra fuente) el mismo texto puede ocupar más.
    const fontSize = topicFontSize(shape.depth)
    const { px, py } = topicPadding(shape.depth)
    ctx.font = fontString(fontSize, shape.depth === 0)
    const caja = { w: Math.max(20, shape.w - px * 2), h: Math.max(12, shape.h - py * 2) }
    const layout = fitText(ctx, shape.text, caja.w, caja.h, fontSize)
    ctx.font = fontString(layout.fontSize, shape.depth === 0)
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'center'
    ctx.fillStyle = topicTextColor(shape)
    const startY = shape.y + shape.h / 2 - ((layout.lines.length - 1) * layout.lineHeight) / 2
    layout.lines.forEach((line, i) => {
      ctx.fillText(line, shape.x + shape.w / 2, startY + i * layout.lineHeight)
    })
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
  }

  // Insignia de rama plegada: circulito con el número de temas escondidos.
  if (shape.hiddenCount) {
    const at = collapseBadgeCenter(shape)
    ctx.beginPath()
    ctx.arc(at.x, at.y, COLLAPSE_BADGE_R, 0, Math.PI * 2)
    ctx.fillStyle = darkDisplay ? '#232329' : '#FFFFFF'
    ctx.fill()
    ctx.strokeStyle = branch
    ctx.lineWidth = 1.5
    ctx.stroke()
    ctx.font = fontString(10, true)
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = displayInk('#424242')
    ctx.fillText(String(shape.hiddenCount), at.x, at.y + 0.5)
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
  }
}

export const COLLAPSE_BADGE_R = 9

/** Dónde va la insignia de plegado: el lado por el que crecen los hijos. */
export function collapseBadgeCenter(shape: TopicShape): { x: number; y: number } {
  const side = shape.side ?? 'right'
  if (side === 'down') return { x: shape.x + shape.w / 2, y: shape.y + shape.h + COLLAPSE_BADGE_R + 2 }
  if (side === 'left') return { x: shape.x - COLLAPSE_BADGE_R - 2, y: shape.y + shape.h / 2 }
  return { x: shape.x + shape.w + COLLAPSE_BADGE_R + 2, y: shape.y + shape.h / 2 }
}

function renderDraw(ctx: CanvasRenderingContext2D, shape: DrawShape): void {
  const path = drawPathFor(shape)
  ctx.save()
  if (shape.rotation) {
    const cx = shape.x + shape.w / 2
    const cy = shape.y + shape.h / 2
    ctx.translate(cx, cy)
    ctx.rotate(shape.rotation)
    ctx.translate(-shape.w / 2, -shape.h / 2)
  } else {
    ctx.translate(shape.x, shape.y)
  }
  if (shape.highlighter) {
    ctx.globalAlpha = darkDisplay ? 0.45 : 0.38
    // "multiply" oscurece: sobre fondo negro el resaltador desaparecería.
    ctx.globalCompositeOperation = darkDisplay ? 'screen' : 'multiply'
  }
  ctx.fillStyle = shape.highlighter ? shape.color : displayInk(shape.color)
  ctx.fill(path)
  ctx.restore()
}

export function geoPath(kind: GeoShape['kind'], w: number, h: number): Path2D {
  const p = new Path2D()
  switch (kind) {
    case 'rectangle':
      p.rect(0, 0, w, h)
      break
    case 'roundRectangle': {
      const r = Math.min(24, w / 4, h / 4)
      p.roundRect(0, 0, w, h, r)
      break
    }
    case 'ellipse':
      p.ellipse(w / 2, h / 2, Math.abs(w / 2), Math.abs(h / 2), 0, 0, Math.PI * 2)
      break
    case 'triangle':
      p.moveTo(w / 2, 0)
      p.lineTo(w, h)
      p.lineTo(0, h)
      p.closePath()
      break
    case 'diamond':
      p.moveTo(w / 2, 0)
      p.lineTo(w, h / 2)
      p.lineTo(w / 2, h)
      p.lineTo(0, h / 2)
      p.closePath()
      break
    case 'hexagon': {
      const q = w / 4
      p.moveTo(q, 0)
      p.lineTo(w - q, 0)
      p.lineTo(w, h / 2)
      p.lineTo(w - q, h)
      p.lineTo(q, h)
      p.lineTo(0, h / 2)
      p.closePath()
      break
    }
    case 'star': {
      const cx = w / 2
      const cy = h / 2
      const outerX = w / 2
      const outerY = h / 2
      const inner = 0.42
      for (let i = 0; i < 10; i++) {
        const angle = (Math.PI / 5) * i - Math.PI / 2
        const rx = i % 2 === 0 ? outerX : outerX * inner
        const ry = i % 2 === 0 ? outerY : outerY * inner
        const x = cx + Math.cos(angle) * rx
        const y = cy + Math.sin(angle) * ry
        if (i === 0) p.moveTo(x, y)
        else p.lineTo(x, y)
      }
      p.closePath()
      break
    }
    case 'arrowRight': {
      const shaft = h * 0.34
      const headW = Math.min(w * 0.4, h * 0.9)
      const midY = h / 2
      p.moveTo(0, midY - shaft)
      p.lineTo(w - headW, midY - shaft)
      p.lineTo(w - headW, 0)
      p.lineTo(w, midY)
      p.lineTo(w - headW, h)
      p.lineTo(w - headW, midY + shaft)
      p.lineTo(0, midY + shaft)
      p.closePath()
      break
    }
    case 'line':
      p.moveTo(0, 0)
      p.lineTo(w, h)
      break
    case 'arrow': {
      p.moveTo(0, 0)
      p.lineTo(w, h)
      break
    }
  }
  return p
}

function renderGeo(ctx: CanvasRenderingContext2D, shape: GeoShape, hideText: boolean): void {
  const path = geoPath(shape.kind, shape.w, shape.h)
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  ctx.lineWidth = shape.strokeWidth
  if (shape.dash === 'dashed') ctx.setLineDash([shape.strokeWidth * 3, shape.strokeWidth * 2.5])
  else if (shape.dash === 'dotted') ctx.setLineDash([0.1, shape.strokeWidth * 2.2])

  const isLine = shape.kind === 'line' || shape.kind === 'arrow'
  if (!isLine && shape.fill && shape.fill !== 'none') {
    ctx.fillStyle = displayInk(shape.fill)
    ctx.fill(path)
  }
  ctx.strokeStyle = displayInk(shape.color)
  ctx.stroke(path)

  if (shape.kind === 'arrow') {
    drawArrowHead(ctx, 0, 0, shape.w, shape.h, shape.strokeWidth, displayInk(shape.color))
  }
  ctx.setLineDash([])

  if (!hideText && shape.text) {
    const pad = 12
    const box = { x: pad, y: pad, w: Math.max(10, shape.w - pad * 2), h: Math.max(10, shape.h - pad * 2) }
    const layout = fitText(ctx, shape.text, box.w, box.h, shape.fontSize ?? 20)
    drawLines(ctx, layout, box, 'center', 'middle', displayInk(shape.textColor ?? shape.color))
  }
}

function drawArrowHead(
  ctx: CanvasRenderingContext2D,
  x0: number, y0: number, x1: number, y1: number,
  strokeWidth: number, color: string,
): void {
  const angle = Math.atan2(y1 - y0, x1 - x0)
  const len = Math.max(10, strokeWidth * 4.5)
  const spread = Math.PI / 7
  ctx.beginPath()
  ctx.moveTo(x1, y1)
  ctx.lineTo(x1 - Math.cos(angle - spread) * len, y1 - Math.sin(angle - spread) * len)
  ctx.moveTo(x1, y1)
  ctx.lineTo(x1 - Math.cos(angle + spread) * len, y1 - Math.sin(angle + spread) * len)
  ctx.strokeStyle = color
  ctx.stroke()
}

function renderNote(ctx: CanvasRenderingContext2D, shape: NoteShape, hideText: boolean): void {
  ctx.save()
  ctx.shadowColor = 'rgba(0,0,0,0.18)'
  ctx.shadowBlur = 10
  ctx.shadowOffsetY = 3
  ctx.fillStyle = shape.color
  const path = new Path2D()
  path.roundRect(0, 0, shape.w, shape.h, 4)
  ctx.fill(path)
  ctx.restore()

  // Franja inferior ligeramente más oscura: da el aspecto de papel doblado.
  ctx.save()
  ctx.globalAlpha = 0.07
  ctx.fillStyle = '#000000'
  ctx.fillRect(0, shape.h - 6, shape.w, 6)
  ctx.restore()

  if (!hideText && shape.text) {
    const pad = 14
    const box = { x: pad, y: pad, w: shape.w - pad * 2, h: shape.h - pad * 2 }
    const layout = fitText(ctx, shape.text, box.w, box.h, Math.min(shape.fontSize, shape.h / 3.2))
    drawLines(ctx, layout, box, shape.align, 'middle', '#1A1A1A')
  }
}

function renderText(ctx: CanvasRenderingContext2D, shape: TextShape, hideText: boolean): void {
  if (hideText || !shape.text) return
  const lines = wrapText(ctx, shape.text, shape.w, shape.fontSize, shape.bold, shape.italic)
  const layout = {
    lines,
    fontSize: shape.fontSize,
    lineHeight: shape.fontSize * 1.25,
    width: shape.w,
    height: lines.length * shape.fontSize * 1.25,
  }
  drawLines(
    ctx,
    layout,
    { x: 0, y: 0, w: shape.w, h: shape.h },
    shape.align,
    'top',
    displayInk(shape.color),
    shape.bold,
    shape.italic,
  )
}

function renderImage(ctx: CanvasRenderingContext2D, shape: ImageShape): void {
  const img = getImage(shape.src)
  const path = new Path2D()
  path.roundRect(0, 0, shape.w, shape.h, 6)
  if (img) {
    ctx.save()
    ctx.shadowColor = 'rgba(0,0,0,0.16)'
    ctx.shadowBlur = 12
    ctx.shadowOffsetY = 3
    ctx.fillStyle = '#ffffff'
    ctx.fill(path)
    ctx.restore()
    ctx.save()
    ctx.clip(path)
    ctx.drawImage(img, 0, 0, shape.w, shape.h)
    ctx.restore()
  } else {
    ctx.save()
    ctx.fillStyle = '#EDEDED'
    ctx.fill(path)
    ctx.strokeStyle = '#D1D1D1'
    ctx.lineWidth = 1
    ctx.stroke(path)
    ctx.fillStyle = '#9A9A9A'
    ctx.font = fontString(14)
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('Cargando imagen…', shape.w / 2, shape.h / 2)
    ctx.restore()
  }
}
